// Sequenzialized situation step with sub-steps and animations
import { useOnboardingStore } from "@/stores/onboardingStore";
import { QuickModeEntry } from "./QuickModeEntry";
import { TriggerStep } from "./TriggerStep";
import { StakesStep } from "./StakesStep";
import { PreviousAttemptsStep } from "./PreviousAttemptsStep";
import { PriorityStep } from "./PriorityStep";
import { QuickModeStep } from "./QuickModeStep";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface StepSituationProps {
  workspaceId: string;
  role?: string;
  title?: string;
  decisionType?: string;
  decisionSubtype?: string;
  onComplete?: () => void;
}

export const StepSituation = ({
  workspaceId,
  role,
  title,
  decisionType,
  decisionSubtype,
  onComplete,
}: StepSituationProps) => {
  const {
    currentSubStep,
    setSubStep,
    quickMode,
    setQuickMode,
    trigger,
    setTrigger,
    stakes,
    setStakes,
    previousAttempts,
    setPreviousAttempts,
    impactScore,
    urgencyScore,
    setScores,
    setSituation,
  } = useOnboardingStore();

  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSubStep]);

  const handleQuickMode = () => {
    setDirection('forward');
    setQuickMode(true);
    setSubStep(1);
  };

  const handleFullMode = () => {
    setDirection('forward');
    setQuickMode(false);
    setSubStep(1);
  };

  const handleQuickModeComplete = (value: string) => {
    setDirection('forward');
    setTrigger(value);
    setStakes(value);
    setPreviousAttempts('');
    setSubStep(5);
  };

  const handleTriggerNext = (value: string) => {
    setDirection('forward');
    setTrigger(value);
    setSubStep(2);
  };

  const handleStakesNext = (value: string) => {
    setDirection('forward');
    setStakes(value);
    setSubStep(3);
  };

  const handlePreviousAttemptsNext = (value: string) => {
    setDirection('forward');
    setPreviousAttempts(value);
    setSubStep(4);
  };

  const handlePreviousAttemptsSkip = () => {
    setDirection('forward');
    setPreviousAttempts('');
    setSubStep(4);
  };

  const handlePriorityNext = (impact: number, urgency: number) => {
    setScores(impact, urgency);
    setSituation(trigger, stakes, previousAttempts);
    onComplete?.();
  };

  const handleBack = () => {
    setDirection('backward');
    if (currentSubStep === 0) {
      return;
    }
    if (quickMode && currentSubStep === 5) {
      setSubStep(1);
    } else {
      setSubStep(Math.max(0, currentSubStep - 1));
    }
  };

  const getAnimationClass = () => {
    return direction === 'forward' 
      ? 'animate-slide-in-from-right' 
      : 'animate-slide-in-from-left';
  };

  const renderCurrentStep = () => {
    if (currentSubStep === 0) {
      return (
        <QuickModeEntry
          onQuickMode={handleQuickMode}
          onFullMode={handleFullMode}
        />
      );
    }

    if (quickMode && currentSubStep === 1) {
      return (
        <QuickModeStep
          initialValue={trigger}
          onNext={handleQuickModeComplete}
          onBack={handleBack}
        />
      );
    }

    if (!quickMode && currentSubStep === 1) {
      return (
        <TriggerStep
          initialValue={trigger}
          jobRole={role}
          onNext={handleTriggerNext}
          onBack={handleBack}
        />
      );
    }

    if (!quickMode && currentSubStep === 2) {
      return (
        <StakesStep
          initialValue={stakes}
          jobRole={role}
          onNext={handleStakesNext}
          onBack={handleBack}
        />
      );
    }

    if (!quickMode && currentSubStep === 3) {
      return (
        <PreviousAttemptsStep
          initialValue={previousAttempts}
          onNext={handlePreviousAttemptsNext}
          onSkip={handlePreviousAttemptsSkip}
          onBack={handleBack}
        />
      );
    }

    if (currentSubStep === 4 || (quickMode && currentSubStep === 5)) {
      return (
        <PriorityStep
          initialImpact={impactScore}
          initialUrgency={urgencyScore}
          onNext={handlePriorityNext}
          onBack={handleBack}
        />
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <div 
        key={`${currentSubStep}-${quickMode}`}
        className={cn(
          "overflow-hidden",
          getAnimationClass()
        )}
      >
        {renderCurrentStep()}
      </div>
    </div>
  );
};
